/**
 * Library Index — Documents_On_Site Prompt 3.
 *
 * Generates a "Library Map" SiteDocument for a site: a single
 * markdown-rendered manifest grouping every uploaded doc by its
 * AI-classified per-report-type relevance. The map lives in
 * `site.documents` under a stable id (`library-index-${siteId}`) so it
 * gets persisted, indexed, and surfaced through the same paths as user
 * uploads — but it's tagged `library-index` so the UI can pin it and
 * the prompt builder can prepend its content as a structured directory
 * before showing the AI individual documents.
 *
 * The build is deterministic (pure function of `site.documents`); the
 * persistence layer is a thin shim that calls `updateSiteDocuments`
 * with the rebuilt index in place. Callers should fire `refreshLibraryIndex`
 * after any doc change — upload, delete, rename, NB-toggle,
 * includeInReport-toggle, or a reclassify-all admin action.
 */
import type { SiteDocument } from '../../types/siteRag';
import { applyLibraryIndex, buildLibraryIndex } from '../../lib/knowledge/libraryIndexDocument';
import { siteDocumentMetadataBasisHash } from '../../lib/siteDocumentMetadata';

// The pure builders moved to `lib/knowledge/libraryIndexDocument` so server
// and worker code can use them without this store-bound module. Re-exported
// here so existing UI imports keep working.
export {
  LIBRARY_INDEX_TAG,
  RELEVANCE_THRESHOLD,
  LIBRARY_INDEX_VERSION,
  isLibraryIndexDoc,
  findLibraryIndex,
  renderLibraryMarkdown,
  buildLibraryIndex,
  applyLibraryIndex,
} from '../../lib/knowledge/libraryIndexDocument';
export type { BuildLibraryIndexArgs } from '../../lib/knowledge/libraryIndexDocument';

/**
 * Persisted refresh — reads the site, builds a fresh map, and writes it
 * back through the store. Idempotent and safe to call from any number
 * of triggers (upload, delete, edit, reclassify). The `getStore` /
 * `getStoreActions` indirections keep this module test-friendly without
 * requiring the React store to be initialised in unit tests.
 */
export interface RefreshDeps {
  /** Returns the site's current document array + name. */
  readSite: (siteId: string) => { name: string; documents: SiteDocument[]; demonstrationProvenance?: unknown } | undefined;
  /** Persists the new documents array. */
  writeSiteDocuments: (siteId: string, documents: SiteDocument[], expectedDocumentsBasis?: string) => Promise<void>;
  /** Override for tests; defaults to new Date().toISOString(). */
  now?: () => string;
}

/**
 * Set of site ids currently being refreshed. The store's
 * `updateSiteDocuments` trigger checks this to avoid recursing when the
 * refresh's own write fires the same trigger. Exported as a read-only
 * predicate (`isRefreshingLibraryIndex`) — callers should never mutate
 * the set directly.
 */
const _inFlight = new Set<string>();
export function isRefreshingLibraryIndex(siteId: string): boolean {
  return _inFlight.has(siteId);
}

export async function refreshLibraryIndex(siteId: string, deps?: RefreshDeps): Promise<void> {
  if (_inFlight.has(siteId)) return;  // already mid-refresh — coalesce
  _inFlight.add(siteId);
  try {
    const resolved = deps ?? (await defaultDeps());
    const site = resolved.readSite(siteId);
    if (!site) {
      console.warn(`[libraryIndex] refresh skipped — site ${siteId} not found in store`);
      return;
    }
    if (!deps && site.demonstrationProvenance) {
      const { runSiteDocumentMaintenance } = await import('../siteDocumentMaintenanceClient');
      await runSiteDocumentMaintenance(siteId, 'refresh-index');
      return;
    }
    const capturedBasis = site.documents.map(siteDocumentMetadataBasisHash).join(',');
    const generatedAt = (resolved.now ?? (() => new Date().toISOString()))();
    const index = buildLibraryIndex({
      siteId,
      siteName: site.name,
      documents: site.documents,
      generatedAt,
    });
    const next = applyLibraryIndex(site.documents, index);
    await resolved.writeSiteDocuments(siteId, next, capturedBasis);
  } finally {
    _inFlight.delete(siteId);
  }
}

/**
 * Lazily resolves the Zustand store at runtime so this module stays
 * tree-shake-friendly and tests can substitute their own deps without
 * the global store ever being touched.
 */
async function defaultDeps(): Promise<RefreshDeps> {
  const { useStore } = await import('../../store');
  return {
    readSite: (siteId) => {
      const s = useStore.getState().siteRag.sites.find(x => x.id === siteId);
      if (!s) return undefined;
      return { name: s.name, documents: s.documents ?? [], demonstrationProvenance: s.demonstrationProvenance };
    },
    writeSiteDocuments: (siteId, documents, expectedDocumentsBasis) => useStore.getState().updateSiteDocuments(siteId, documents, expectedDocumentsBasis),
  };
}

/**
 * Debounced refresh — coalesces bursts of triggers (e.g., a 50-file
 * batch upload firing one event per file) into a single rebuild after
 * `delayMs` of quiet. Per-site so different sites don't share a timer.
 *
 * Default cadence is 30s (Documents_On_Site Prompt 7-B): tuned so the
 * map is fresh enough to be useful for the user's "next move" without
 * burning regenerations on every keystroke / NB-toggle. Users in a
 * hurry can hit the manual "Refresh library map" button on the
 * library's pinned 📋 row.
 */
const _pending = new Map<string, ReturnType<typeof setTimeout>>();
export function refreshLibraryIndexDebounced(siteId: string, delayMs = 30_000, deps?: RefreshDeps): void {
  const existing = _pending.get(siteId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    _pending.delete(siteId);
    refreshLibraryIndex(siteId, deps).catch(e => {
      console.warn(`[libraryIndex] refresh failed for site ${siteId}:`, (e as Error)?.message);
    });
  }, delayMs);
  _pending.set(siteId, timer);
}
